<template>
  <div class="dashboard-container" :class="{ 'fullscreen': isFullscreen }">
    <div class="dashboard-header">
      <h2 class="dashboard-title">超长母乳期监控大屏</h2>
      <div class="header-actions">
        <span class="refresh-timer" v-if="!isSilentRefreshing">
          <a-icon type="sync" :spin="isSilentRefreshing" />
          {{ refreshCountdown }}s后刷新
        </span>
        <span class="refresh-timer refreshing" v-else>
          <a-icon type="sync" spin />
          刷新中...
        </span>
        <a-select
          v-model="selectedFarmId"
          placeholder="选择农场"
          style="width: 180px;"
          class="farm-selector"
          @change="onFarmChange"
          allow-clear>
          <a-select-option v-for="farm in farmList" :key="farm.id" :value="farm.id">
            {{ farm.name }}
          </a-select-option>
        </a-select>
        <a-button 
          :icon="isFullscreen ? 'fullscreen-exit' : 'fullscreen'" 
          @click="toggleFullscreen"
          type="primary">
          {{ isFullscreen ? '退出全屏' : '全屏查看' }}
        </a-button>
      </div>
    </div>
    
    <div class="dashboard-content">
      <div class="dashboard-row">
        <!-- 年度产奶热度图 -->
        <div class="card">
          <div class="card-header">
            <h3>农场年度产奶热度图</h3>
            <div class="card-header-actions">
              <a-select v-model="heatmapYear" placeholder="选择年份" @change="loadHeatmapData" style="width: 120px;">
                <a-select-option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</a-select-option>
              </a-select>
              <a-button type="primary" @click="loadHeatmapData" style="margin-left: 10px;">刷新</a-button>
            </div>
          </div>
          <div class="card-body">
            <HeatmapCalendar 
              :data="heatmapData" 
              :year="heatmapYear"
              :loading="heatmapLoading"
              :bordered="false" />
          </div>
        </div>

        <!-- 产奶个体数量曲线 -->
        <div class="card">
          <div class="card-header">
            <h3>产奶个体数量曲线</h3>
            <div class="card-header-actions">
              <a-range-picker
                v-model="individualCountDateRange"
                @change="loadIndividualCountData"
                style="width: 240px;" />
              <a-button type="primary" @click="loadIndividualCountData" style="margin-left: 10px;">刷新</a-button>
            </div>
          </div>
          <div class="card-body">
            <ChartLine 
              :data="individualCountData" 
              :loading="individualCountLoading" 
              :options="{ title: '产奶个体数量趋势', yAxisLabel: '个体数量' }" />
          </div>
        </div>
      </div>

      <div class="dashboard-row">
        <!-- 产奶量曲线 -->
        <div class="card">
          <div class="card-header">
            <h3>产奶量曲线</h3>
            <div class="card-header-actions">
              <a-range-picker
                v-model="productionAmountDateRange"
                @change="loadProductionAmountData"
                style="width: 240px;" />
              <a-button type="primary" @click="loadProductionAmountData" style="margin-left: 10px;">刷新</a-button>
            </div>
          </div>
          <div class="card-body">
            <ChartLine 
              :data="productionAmountData" 
              :loading="productionAmountLoading" 
              :options="{ title: '产奶量趋势', yAxisLabel: '产奶量(kg)' }" />
          </div>
        </div>

        <!-- 产奶时长排名 -->
        <div class="card">
          <div class="card-header">
            <h3>产奶时长排名</h3>
            <div class="card-header-actions">
              <span class="input-label">显示条数：</span>
              <a-input-number v-model="rankingLimit" :min="1" :max="100" @change="loadRankingData" style="width: 80px;" />
              <a-button type="primary" @click="loadRankingData" style="margin-left: 10px;">刷新</a-button>
            </div>
          </div>
          <div class="card-body">
            <a-table 
              :columns="rankingColumns"
              :data-source="rankingData" 
              :loading="rankingLoading" 
              :pagination="false"
              :scroll="{ x: 800 }"
              size="small">
              <template slot="vid" slot-scope="text, record">
                <ItemButton :text="record.vid" :id="record.item_id" :is_dead="record.is_dead" />
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import HeatmapCalendar from '@/components/Widgets/HeatmapCalendar.vue'
import ChartLine from '@/components/Charts/ChartLine.vue'
import ItemButton from '@/views/components/ItemButton.vue'

export default {
  name: 'ProductionMonitoringDashboard',
  components: {
    HeatmapCalendar,
    ChartLine,
    ItemButton
  },
  data() {
    return {
      heatmapYear: new Date().getFullYear(),
      heatmapData: [],
      heatmapLoading: false,
      
      individualCountDateRange: [],
      individualCountData: [],
      individualCountLoading: false,
      
      productionAmountDateRange: [],
      productionAmountData: [],
      productionAmountLoading: false,
      
      rankingLimit: 10,
      rankingData: [],
      rankingLoading: false,
      
      yearOptions: [],
      isFullscreen: false,
      
      // Farm selection
      farmList: [],
      selectedFarmId: undefined,
      
      // Auto refresh
      refreshTimer: null,
      refreshCountdown: 60,
      isSilentRefreshing: false,
      countdownTimer: null,
      
      rankingColumns: [
        { title: 'VID', dataIndex: 'vid', width: 180, scopedSlots: { customRender: 'vid' } },
        { title: '个体名称', dataIndex: 'item_name', width: 200 },
        { title: '连续产奶天数', dataIndex: 'continuous_days', width: 150 },
        { title: '开始日期', dataIndex: 'start_date', width: 120 },
        { title: '结束日期', dataIndex: 'end_date', width: 120 }
      ]
    }
  },
  computed: {
    ...mapGetters(['currentTenant'])
  },
  async mounted() {
    await this.initData()
    await this.loadFarmList()
    await this.loadAllData()
    
    // Start auto refresh timer
    this.startAutoRefresh()
    
    // 监听ESC键退出全屏
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    // 移除事件监听器
    document.removeEventListener('keydown', this.handleKeyDown)
    
    // Clear timers
    this.stopAutoRefresh()
  },
  methods: {
    async initData() {
      // 初始化年份选项
      const currentYear = new Date().getFullYear()
      this.yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)
      
      // 初始化默认日期范围（最近30天）
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)
      this.individualCountDateRange = [startDate, endDate]
      this.productionAmountDateRange = [startDate, endDate]
    },
    
    async loadFarmList() {
      try {
        const response = await this.$api.get_farms({ page_size: 100 })
        this.farmList = response.data?.list || []
      } catch (error) {
        console.error('获取农场列表失败:', error)
        this.farmList = []
      }
    },
    
    onFarmChange() {
      // 当农场选择变化时，重新加载所有数据
      this.loadAllData()
    },
    
    async loadAllData(silent = false) {
      // 确保数据初始化完成后再加载
      if (!this.individualCountDateRange?.length || !this.productionAmountDateRange?.length) {
        await this.initData();
      }
      
      // 使用 try-catch 包装每个加载方法，避免单个失败影响其他
      const loadMethods = [
        () => this.loadHeatmapData(silent),
        () => this.loadIndividualCountData(silent),
        () => this.loadProductionAmountData(silent),
        () => this.loadRankingData(silent)
      ];
      
      await Promise.all(loadMethods.map(method => method().catch(err => {
        // 静默处理错误，避免影响其他数据加载
        if (!silent) {
          console.error('加载数据失败:', err);
        }
      })));
    },
    
    // Auto refresh methods
    startAutoRefresh() {
      // Countdown timer (update every second)
      this.countdownTimer = setInterval(() => {
        this.refreshCountdown--
        if (this.refreshCountdown <= 0) {
          this.refreshCountdown = 60
        }
      }, 1000)
      
      // Data refresh timer (every 60 seconds)
      this.refreshTimer = setInterval(async () => {
        await this.silentRefresh()
      }, 60000)
    },
    
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    
    async silentRefresh() {
      this.isSilentRefreshing = true
      try {
        await this.loadAllData(true)
      } finally {
        this.isSilentRefreshing = false
        this.refreshCountdown = 60
      }
    },
    
    async loadHeatmapData(silent = false) {
      if (!this.heatmapYear) return
      
      if (!silent) this.heatmapLoading = true
      try {
        const params = {
          year: this.heatmapYear
        }
        
        // Add farm_id if selected and valid
        if (this.selectedFarmId && Number.isFinite(this.selectedFarmId)) {
          params.farm_id = this.selectedFarmId
        }
        
        const response = await this.$api.getAnnualHeatmap(params)
        this.heatmapData = response.data || []
      } catch (error) {
        // 静默处理错误
        if (!silent) {
          console.error('获取热度图数据失败:', error)
          this.heatmapData = []
        }
      } finally {
        if (!silent) this.heatmapLoading = false
      }
    },
    
    async loadIndividualCountData(silent = false) {
      const [startDate, endDate] = this.individualCountDateRange || []
      if (!startDate || !endDate) {
        // 日期范围未初始化，跳过加载
        return;
      }
      
      if (!silent) this.individualCountLoading = true
      try {
        const params = {
          start_date: this.formatDate(startDate),
          end_date: this.formatDate(endDate),
          group_by: 'day'
        }
        
        // Add farm_id if selected and valid
        if (this.selectedFarmId && Number.isFinite(this.selectedFarmId)) {
          params.farm_id = this.selectedFarmId
        }
        
        const response = await this.$api.getIndividualCountCurve(params)
        this.individualCountData = response.data || []
      } catch (error) {
        // 静默处理错误
        if (!silent) {
          console.error('获取个体数量曲线数据失败:', error)
          this.individualCountData = []
        }
      } finally {
        if (!silent) this.individualCountLoading = false
      }
    },
    
    async loadProductionAmountData(silent = false) {
      const [startDate, endDate] = this.productionAmountDateRange || []
      if (!startDate || !endDate) {
        // 日期范围未初始化，跳过加载
        return;
      }
      
      if (!silent) this.productionAmountLoading = true
      try {
        const params = {
          start_date: this.formatDate(startDate),
          end_date: this.formatDate(endDate),
          group_by: 'day'
        }
        
        // Add farm_id if selected and valid
        if (this.selectedFarmId && Number.isFinite(this.selectedFarmId)) {
          params.farm_id = this.selectedFarmId
        }
        
        const response = await this.$api.getProductionAmountCurve(params)
        // 处理实际记录和估算记录的数据
        if (response.data) {
          // 合并实际和估算记录的数据
          this.productionAmountData = [...(response.data.actual || []), ...(response.data.estimated || [])]
        } else {
          if (!silent) this.productionAmountData = []
        }
      } catch (error) {
        // 静默处理错误
        if (!silent) {
          console.error('获取产奶量曲线数据失败:', error)
          this.productionAmountData = []
        }
      } finally {
        if (!silent) this.productionAmountLoading = false
      }
    },
    
    async loadRankingData(silent = false) {
      if (!silent) this.rankingLoading = true
      try {
        const params = {
          limit: this.rankingLimit
        }
        
        // Add farm_id if selected and valid
        if (this.selectedFarmId && Number.isFinite(this.selectedFarmId)) {
          params.farm_id = this.selectedFarmId
        }
        
        const response = await this.$api.getProducingDurationRanking(params)
        this.rankingData = response.data || []
      } catch (error) {
        // 静默处理错误
        if (!silent) {
          console.error('获取产奶时长排名数据失败:', error)
          this.rankingData = []
        }
      } finally {
        if (!silent) this.rankingLoading = false
      }
    },
    
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen()
      } else {
        this.exitFullscreen()
      }
    },
    
    enterFullscreen() {
      const element = document.querySelector('.dashboard-container')
      if (element.requestFullscreen) {
        element.requestFullscreen()
      } else if (element.mozRequestFullScreen) { /* Firefox */
        element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) { /* Chrome, Safari & Opera */
        element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) { /* IE/Edge */
        element.msRequestFullscreen()
      }
      this.isFullscreen = true
    },
    
    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.mozCancelFullScreen) { /* Firefox */
        document.mozCancelFullScreen()
      } else if (document.webkitCancelFullScreen) { /* Chrome, Safari & Opera */
        document.webkitCancelFullScreen()
      } else if (document.msExitFullscreen) { /* IE/Edge */
        document.msExitFullscreen()
      }
      this.isFullscreen = false
    },
    
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.isFullscreen) {
        this.exitFullscreen()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  padding: 16px;
  background-color: #f0f2f5;
  height: calc(100vh - 78px);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    padding: 16px;
    overflow: hidden;
    background-color: #fff;
    
    .dashboard-content {
      height: calc(100vh - 70px);
    }
  }
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.dashboard-title {
  color: rgba(0, 0, 0, 0.85);
  font-size: 20px;
  font-weight: bold;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  
  .ant-btn {
    margin-left: 0;
  }
  
  .farm-selector {
    min-width: 180px;
  }
}

.refresh-timer {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  gap: 4px;
  
  &.refreshing {
    color: #1890ff;
  }
}

.input-label {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.dashboard-row {
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.card {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
  
  h3 {
    margin: 0;
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    font-weight: 500;
  }
  
  .card-header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    
    .ant-select, .ant-picker, .ant-input-number {
      width: auto;
    }
  }
}

.card-body {
  padding: 12px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  
  ::v-deep .ant-table {
    .ant-table-thead > tr > th {
      background-color: #fafafa;
      font-weight: 500;
    }
    
    .ant-table-tbody > tr:hover > td {
      background-color: #f5f5f5;
    }
  }
  
  ::v-deep .ant-table-wrapper {
    .ant-table-body {
      overflow-x: auto;
    }
  }
  
  // Make charts fill available space
  ::v-deep .heatmap-calendar-widget {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    
    .heatmap-content {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
    
    .heatmap-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }
  
  ::v-deep .chart-container {
    flex: 1;
    min-height: 0;
    height: 100% !important;
  }
}

@media (max-width: 1200px) {
  .dashboard-container {
    height: auto;
    min-height: calc(100vh - 78px);
    overflow: auto;
  }
  
  .dashboard-row {
    flex-direction: column;
    flex: none;
  }
  
  .card {
    flex: none;
    min-height: 300px;
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    padding: 10px;
    height: auto;
    min-height: calc(100vh - 78px);
  }
  
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    
    .header-actions {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
  }
  
  .card-header {
    flex-direction: column;
    align-items: stretch;
    
    .card-header-actions {
      margin-top: 8px;
      justify-content: flex-end;
    }
  }
}
</style>